import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle, PiggyBank, DollarSign } from 'lucide-react';
import { PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'personal_loan_data';

export const PersonalLoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).loanAmount : 50000;
  });

  const [interestRate, setInterestRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).interestRate : 12;
  });

  const [tenure, setTenure] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).tenure : 36;
  });

  const [processingFee, setProcessingFee] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).processingFee : 1000;
  });

  const [prepayment, setPrepayment] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).prepayment : 0;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      loanAmount,
      interestRate,
      tenure,
      processingFee,
      prepayment
    }));
  }, [loanAmount, interestRate, tenure, processingFee, prepayment]);

  const calculateEMI = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = (parseFloat(interestRate) || 0) / 12 / 100;
    const months = parseFloat(tenure) || 1;
    const fee = parseFloat(processingFee) || 0;

    if (rate === 0) {
      const emi = principal / months;
      return {
        emi,
        totalPayment: principal + fee,
        totalInterest: 0,
        totalCost: principal + fee,
        principalAmount: principal,
        processingFee: fee
      };
    }

    const emi = (principal * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    const totalPayment = emi * months;
    const totalInterest = totalPayment - principal;
    const totalCost = totalPayment + fee;

    return {
      emi,
      totalPayment,
      totalInterest,
      totalCost,
      principalAmount: principal,
      processingFee: fee
    };
  };

  const calculatePrepaymentImpact = () => {
    const results = calculateEMI();
    const prepay = parseFloat(prepayment) || 0;
    
    if (prepay === 0) return null;

    const principal = parseFloat(loanAmount) || 0;
    const newPrincipal = principal - prepay;
    const rate = (parseFloat(interestRate) || 0) / 12 / 100;
    const months = parseFloat(tenure) || 1;

    if (newPrincipal <= 0) {
      return {
        newEMI: 0,
        newTotalInterest: 0,
        interestSaved: results.totalInterest,
        timeSaved: months
      };
    }

    const newEMI = (newPrincipal * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    const newTotalPayment = newEMI * months;
    const newTotalInterest = newTotalPayment - newPrincipal;
    const interestSaved = results.totalInterest - newTotalInterest;

    return {
      newEMI,
      newTotalInterest,
      interestSaved,
      timeSaved: 0
    };
  };

  const generateAmortizationSchedule = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = (parseFloat(interestRate) || 0) / 12 / 100;
    const months = parseFloat(tenure) || 1;
    const emi = calculateEMI().emi;

    let balance = principal;
    const schedule = [];

    for (let month = 1; month <= Math.min(months, 12); month++) {
      const interestPayment = balance * rate;
      const principalPayment = emi - interestPayment;
      balance -= principalPayment;

      schedule.push({
        month,
        emi: emi,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance)
      });
    }

    return schedule;
  };

  const generateTenureComparison = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = (parseFloat(interestRate) || 0) / 12 / 100;
    const baseTenure = parseFloat(tenure) || 12;

    const tenures = [
      baseTenure * 0.5,
      baseTenure * 0.75,
      baseTenure,
      baseTenure * 1.25,
      baseTenure * 1.5
    ];

    return tenures.map(months => {
      const emi = (principal * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
      const totalPayment = emi * months;
      const totalInterest = totalPayment - principal;

      return {
        tenure: `${Math.round(months)} months`,
        emi: Math.round(emi),
        totalInterest: Math.round(totalInterest)
      };
    });
  };

  const generateOutstandingBalance = () => {
    const principal = parseFloat(loanAmount) || 0;
    const rate = (parseFloat(interestRate) || 0) / 12 / 100;
    const months = parseFloat(tenure) || 1;
    const emi = calculateEMI().emi;

    let balance = principal;
    const data = [];
    const step = Math.max(1, Math.floor(months / 10));

    data.push({ month: 0, balance: principal });

    for (let month = step; month <= months; month += step) {
      for (let i = 0; i < step; i++) {
        const interestPayment = balance * rate;
        const principalPayment = emi - interestPayment;
        balance -= principalPayment;
      }
      data.push({ month, balance: Math.max(0, balance) });
    }

    return data;
  };

  const results = calculateEMI();
  const prepaymentResults = calculatePrepaymentImpact();
  const amortizationSchedule = generateAmortizationSchedule();
  const tenureComparison = generateTenureComparison();
  const outstandingBalance = generateOutstandingBalance();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const pieData = [
    { name: 'Principal', value: results.principalAmount, fill: 'hsl(var(--primary))' },
    { name: 'Interest', value: results.totalInterest, fill: 'hsl(var(--destructive))' }
  ];

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Personal Loan Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is a Personal Loan?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  A personal loan is an unsecured loan that provides quick access to funds for various needs. 
                  Understanding EMI calculations helps you plan repayments and choose the right loan terms for your budget.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Loan Parameters</CardTitle>
                  <CardDescription>Enter your loan details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="loanAmount" className="text-sm font-medium">
                      Loan Amount ($)
                    </Label>
                    <Input
                      id="loanAmount"
                      type="number"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(e.target.value)}
                      placeholder="50000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interestRate" className="text-sm font-medium">
                      Interest Rate (% per year)
                    </Label>
                    <Input
                      id="interestRate"
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      placeholder="12"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tenure" className="text-sm font-medium">
                      Loan Tenure (Months)
                    </Label>
                    <Input
                      id="tenure"
                      type="number"
                      value={tenure}
                      onChange={(e) => setTenure(e.target.value)}
                      placeholder="36"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">{(tenure / 12).toFixed(1)} years</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="processingFee" className="text-sm font-medium">
                      Processing Fee ($)
                    </Label>
                    <Input
                      id="processingFee"
                      type="number"
                      value={processingFee}
                      onChange={(e) => setProcessingFee(e.target.value)}
                      placeholder="1000"
                      className="text-base"
                    />
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="prepayment" className="text-sm font-medium">
                      Prepayment Amount ($)
                    </Label>
                    <Input
                      id="prepayment"
                      type="number"
                      value={prepayment}
                      onChange={(e) => setPrepayment(e.target.value)}
                      placeholder="0"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Optional: See prepayment impact</p>
                  </div>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Loan Summary</CardTitle>
                    <CardDescription>Your monthly payment breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly EMI</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.emi)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Pay this every month</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Interest</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive">
                          {formatCurrency(results.totalInterest)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Over loan period</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Payable</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.totalPayment)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Principal + Interest</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Cost</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.totalCost)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Including fees</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {prepaymentResults && (
                  <Card className="border-border/50 shadow-md bg-secondary-light/30">
                    <CardHeader>
                      <CardTitle className="font-display text-secondary">Prepayment Impact</CardTitle>
                      <CardDescription>See how prepaying saves money</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-card border border-border">
                          <p className="text-sm text-muted-foreground mb-1">New Monthly EMI</p>
                          <p className="text-2xl font-display font-bold text-secondary">
                            {formatCurrency(prepaymentResults.newEMI)}
                          </p>
                        </div>
                        <div className="p-4 rounded-lg bg-card border border-border">
                          <p className="text-sm text-muted-foreground mb-1">Interest Saved</p>
                          <p className="text-2xl font-display font-bold text-secondary">
                            {formatCurrency(prepaymentResults.interestSaved)}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-4">
                        💡 Prepaying {formatCurrency(prepayment)} saves you {formatCurrency(prepaymentResults.interestSaved)} in interest!
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">EMI Breakdown</CardTitle>
                <CardDescription className="text-base">
                  Principal vs Interest composition
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Outstanding Balance Over Time</CardTitle>
                  <CardDescription>
                    How your loan balance decreases
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={outstandingBalance}>
                        <defs>
                          <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Month', position: 'insideBottom', offset: -5 }}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="balance" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorBalance)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Tenure vs EMI Comparison</CardTitle>
                  <CardDescription>
                    How tenure affects your monthly payment
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={tenureComparison}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="tenure" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="emi" fill="hsl(var(--secondary))" name="Monthly EMI" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">First Year Amortization Schedule</CardTitle>
                <CardDescription>
                  Monthly principal vs interest breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={amortizationSchedule}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="month" 
                        stroke="hsl(var(--muted-foreground))"
                        label={{ value: 'Month', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="principal" stackId="a" fill="hsl(var(--primary))" name="Principal" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="interest" stackId="a" fill="hsl(var(--destructive))" name="Interest" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Personal Loans</CardTitle>
                <CardDescription className="text-base">
                  Learn how to manage your loan effectively and save money
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Your Monthly EMI: {formatCurrency(results.emi)}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    EMI (Equated Monthly Installment) is your fixed monthly payment that includes both principal and interest. 
                    Your {formatCurrency(loanAmount)} loan at {interestRate}% interest for {tenure} months requires 
                    a monthly payment of <strong className="text-secondary">{formatCurrency(results.emi)}</strong>.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">EMI Formula = [P × R × (1+R)^N] / [(1+R)^N - 1]</p>
                    <p className="text-muted-foreground">
                      P = Principal, R = Monthly Interest Rate, N = Number of Months
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Total Interest Cost: {formatCurrency(results.totalInterest)}
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Over {tenure} months, you'll pay <strong className="text-destructive">{formatCurrency(results.totalInterest)}</strong> in interest. 
                    This is {((results.totalInterest / results.principalAmount) * 100).toFixed(1)}% of your principal amount.
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium">Interest Burden:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        results.totalInterest / results.principalAmount < 0.2 ? 'bg-secondary/20 text-secondary' :
                        results.totalInterest / results.principalAmount < 0.4 ? 'bg-accent/20 text-accent' :
                        'bg-destructive/20 text-destructive'
                      }`}>
                        {results.totalInterest / results.principalAmount < 0.2 ? 'Low' :
                         results.totalInterest / results.principalAmount < 0.4 ? 'Moderate' :
                         'High'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {results.totalInterest / results.principalAmount < 0.2 ? 
                        'Great! Your interest burden is low due to short tenure or low rate.' :
                       results.totalInterest / results.principalAmount < 0.4 ?
                        'Acceptable interest cost. Consider prepayment to reduce further.' :
                        'High interest burden. Strongly consider prepayment or refinancing at a lower rate.'}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    The Power of Prepayment
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Prepaying your loan can dramatically reduce interest costs. Even small prepayments early in the 
                    loan tenure have a compounding effect, as they reduce the principal on which future interest is calculated.
                  </p>
                  <div className="space-y-3 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">💡 Early Prepayment Strategy</h4>
                      <p className="text-muted-foreground">
                        Prepay during the first 1/3 of your loan tenure when interest component is highest. 
                        A {formatCurrency(5000)} prepayment in month 6 saves more than the same amount in month 30.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">💡 Windfall Strategy</h4>
                      <p className="text-muted-foreground">
                        Use bonuses, tax refunds, or other windfalls to make lump sum prepayments. 
                        Check if your lender charges prepayment penalties (typically 2-5% of outstanding).
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Tenure vs EMI Trade-off
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Choosing the right loan tenure is a balance between affordability and interest costs. 
                    Shorter tenures mean higher EMIs but lower total interest. Longer tenures offer lower EMIs but cost more overall.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-secondary-light/50 border border-secondary/20">
                      <h4 className="font-semibold text-secondary mb-2">✅ Shorter Tenure Benefits</h4>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Significantly lower total interest</li>
                        <li>Debt-free faster</li>
                        <li>Better for disciplined savers</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light/50 border border-accent/20">
                      <h4 className="font-semibold text-accent mb-2">✅ Longer Tenure Benefits</h4>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Lower monthly payment burden</li>
                        <li>Better cash flow management</li>
                        <li>Can prepay to reduce tenure later</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Personal Loan Cost Warnings
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">High Interest Rates:</strong> Personal loans are unsecured, 
                        so rates (10-20%) are higher than secured loans like mortgages (6-8%). Only borrow if necessary.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Hidden Fees:</strong> Watch for processing fees (1-3% of loan), 
                        prepayment penalties, and late payment charges. Read the fine print carefully.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Credit Score Impact:</strong> Missing EMIs damages your credit score 
                        severely. Ensure the EMI is no more than 40% of your monthly income.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Debt Trap:</strong> Avoid taking multiple personal loans simultaneously. 
                        This creates a debt spiral that's hard to escape.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Comparison with Secured Loans
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-3 text-left font-semibold">Feature</th>
                          <th className="p-3 text-left font-semibold">Personal Loan</th>
                          <th className="p-3 text-left font-semibold">Home/Auto Loan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <tr>
                          <td className="p-3 font-medium">Interest Rate</td>
                          <td className="p-3 text-destructive">10-20% (Higher)</td>
                          <td className="p-3 text-secondary">6-10% (Lower)</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">Collateral</td>
                          <td className="p-3">None required</td>
                          <td className="p-3">Property/Vehicle</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">Approval Time</td>
                          <td className="p-3 text-secondary">1-3 days (Fast)</td>
                          <td className="p-3">7-30 days</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">Loan Amount</td>
                          <td className="p-3">$1k - $50k</td>
                          <td className="p-3">$50k - $500k+</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">Prepayment</td>
                          <td className="p-3 text-destructive">May have penalties</td>
                          <td className="p-3 text-secondary">Usually flexible</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    💡 Tip: If you own assets, secured loans are usually cheaper. Use personal loans for emergencies or 
                    when you need quick funds without collateral.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PersonalLoanCalculator;
